/**
 * AllInventoryTransactions
 * Unified transaction history for all 6 inventory types in one view.
 * Merges products, chemicals, caps, bottles, labels, supplies sorted by date.
 */

import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Select, MenuItem, useTheme, alpha
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getInventoryConfig } from '../shared/inventoryConfig';
import { useLocations } from '../shared/useInventoryData';
import useTransactions from '../transactions-components/useTransactions';
import useProductTransactions from '../transactions-components/useProductTransactions';
import Filters from '../transactions-components/Filters';
import EmptyState from '../transactions-components/EmptyState';
import ViewTransactionDialogue from '../transactions-components/ViewTransactionDialogue';
import ProductViewTransactionDialogue from '../transactions-components/ProductViewTransactionDialogue';
import AllTransactionsList from './all-inventory-transactions-components/AllTransactionsList';

// ── Tagging helper ─────────────────────────────────────────────────────────────

function tagTransactions(txArray, inventoryType) {
  const config = inventoryType !== 'products' ? getInventoryConfig(inventoryType) : null;
  return txArray.map(tx => ({ ...tx, _inventoryType: inventoryType, _config: config }));
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AllInventoryTransactions({ companyId }) {
  const theme = useTheme();

  // All 6 hooks unconditionally (React rules — no conditional hooks)
  const productHook   = useProductTransactions(companyId);
  const chemicalsHook = useTransactions('chemicals', companyId);
  const capsHook      = useTransactions('caps',      companyId);
  const bottlesHook   = useTransactions('bottles',   companyId);
  const labelsHook    = useTransactions('labels',    companyId);
  const suppliesHook  = useTransactions('supplies',  companyId);

  const { locations }                    = useLocations(companyId);
  const { timeFormat, updateTimeFormat } = productHook;

  // Filter states
  const [searchQuery,      setSearchQuery]      = useState('');
  const [selectedType,     setSelectedType]      = useState('');
  const [selectedLocation, setSelectedLocation]  = useState('');
  const [fromDate,         setFromDate]          = useState('');
  const [toDate,           setToDate]            = useState('');

  // Pagination
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // View dialog
  const [viewDialog, setViewDialog] = useState({ open: false, transaction: null });

  const loading = productHook.loading || chemicalsHook.loading || capsHook.loading ||
                  bottlesHook.loading || labelsHook.loading    || suppliesHook.loading;

  // Merge + tag + sort all transactions by date descending
  const allTransactions = useMemo(() => {
    const tagged = [
      ...tagTransactions(productHook.transactions   || [], 'products'),
      ...tagTransactions(chemicalsHook.transactions || [], 'chemicals'),
      ...tagTransactions(capsHook.transactions      || [], 'caps'),
      ...tagTransactions(bottlesHook.transactions   || [], 'bottles'),
      ...tagTransactions(labelsHook.transactions    || [], 'labels'),
      ...tagTransactions(suppliesHook.transactions  || [], 'supplies'),
    ];
    return tagged.sort((a, b) => {
      const dateA = new Date(a.transaction_date || a.created_at || 0);
      const dateB = new Date(b.transaction_date || b.created_at || 0);
      return dateB - dateA;
    });
  }, [
    productHook.transactions, chemicalsHook.transactions, capsHook.transactions,
    bottlesHook.transactions, labelsHook.transactions,    suppliesHook.transactions
  ]);

  // Apply filters
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter(tx => {
      if (searchQuery) {
        const q        = searchQuery.toLowerCase();
        const isProd   = tx._inventoryType === 'products';
        const itemName = (isProd ? tx.products?.name : tx[tx._config?.tableName]?.name)?.toLowerCase() || '';
        const itemSku  = (isProd ? tx.products?.sku  : tx[tx._config?.tableName]?.sku)?.toLowerCase()  || '';
        const variant  = isProd ? (tx.variants?.name?.toLowerCase() || '') : '';
        const notes    = tx.notes?.toLowerCase()         || '';
        const typeStr  = tx._inventoryType?.toLowerCase() || '';
        if (![itemName, itemSku, variant, notes, typeStr].some(f => f.includes(q))) return false;
      }
      if (selectedType && tx.transaction_type !== selectedType) return false;
      if (selectedLocation) {
        const matches =
          tx.location_id      === selectedLocation ||
          tx.from_location_id === selectedLocation ||
          tx.to_location_id   === selectedLocation;
        if (!matches) return false;
      }
      if (fromDate) {
        const txDate = new Date(tx.transaction_date || tx.created_at).toISOString().split('T')[0];
        if (txDate < fromDate) return false;
      }
      if (toDate) {
        const txDate = new Date(tx.transaction_date || tx.created_at).toISOString().split('T')[0];
        if (txDate > toDate) return false;
      }
      return true;
    });
  }, [allTransactions, searchQuery, selectedType, selectedLocation, fromDate, toDate]);

  React.useEffect(() => { setPage(0); }, [filteredTransactions.length]);

  const paginatedTransactions = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTransactions.slice(start, start + rowsPerPage);
  }, [filteredTransactions, page, rowsPerPage]);

  const handleRefreshAll = () => {
    productHook.refresh();
    chemicalsHook.refresh();
    capsHook.refresh();
    bottlesHook.refresh();
    labelsHook.refresh();
    suppliesHook.refresh();
  };

  const clearFilters = () => {
    setSearchQuery(''); setSelectedType(''); setSelectedLocation('');
    setFromDate('');    setToDate('');
  };

  const isSelectedProduct = viewDialog.transaction?._inventoryType === 'products';

  return (
    <Box>
      {/* Header card */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        mb: 3, p: 3, borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative', overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""', position: 'absolute', top: 0, right: 0,
          width: '200px', height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%', transform: 'translate(50%, -50%)', zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
            }} />
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem' }}>
              Transaction History
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.8rem', lineHeight: 1.2, mb: 1 }}>
            All Inventory Transactions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem', mb: 2.5, maxWidth: '440px' }}>
            View addition, transfer, usage, and adjustment history across all inventory types
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={`${filteredTransactions.length} Transactions`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}
            />
            <Chip
              label={`${filteredTransactions.filter(t => t.transaction_type === 'inventory_creation').length} Additions`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}
            />
            <Chip
              label={`${filteredTransactions.filter(t => t.transaction_type === 'transfer').length} Transfers`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
            />
            <Chip
              label={`${filteredTransactions.filter(t => t.transaction_type === 'usage').length} Usage`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <Select
            value={timeFormat}
            onChange={(e) => updateTimeFormat(e.target.value)}
            size="small"
            sx={{
              minWidth: 110, borderRadius: 2, bgcolor: 'white', fontWeight: 500,
              '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` },
              '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` }
            }}
          >
            <MenuItem value="12h">12-hour</MenuItem>
            <MenuItem value="24h">24-hour</MenuItem>
          </Select>

          <Tooltip title="Refresh transactions">
            <IconButton
              onClick={handleRefreshAll}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                color: 'primary.main',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15), transform: 'rotate(180deg)', transition: 'all 0.4s ease' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Filters
        searchQuery={searchQuery}           onSearchChange={setSearchQuery}
        selectedType={selectedType}         onTypeChange={setSelectedType}
        selectedLocation={selectedLocation} onLocationChange={setSelectedLocation}
        fromDate={fromDate}                 onFromDateChange={setFromDate}
        toDate={toDate}                     onToDateChange={setToDate}
        locations={locations || []}
      />

      {/* List or Empty State */}
      {filteredTransactions.length === 0 && !loading ? (
        <EmptyState
          hasTransactions={allTransactions.length > 0}
          onClearFilters={clearFilters}
        />
      ) : (
        <AllTransactionsList
          transactions={paginatedTransactions}
          totalCount={filteredTransactions.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
          timeFormat={timeFormat}
          onViewTransaction={(tx) => setViewDialog({ open: true, transaction: tx })}
        />
      )}

      {/* View Dialogs — one per type, only one open at a time */}
      <ProductViewTransactionDialogue
        open={viewDialog.open && isSelectedProduct}
        onClose={() => setViewDialog({ open: false, transaction: null })}
        transaction={viewDialog.transaction}
        timeFormat={timeFormat}
      />
      <ViewTransactionDialogue
        open={viewDialog.open && !isSelectedProduct}
        onClose={() => setViewDialog({ open: false, transaction: null })}
        transaction={viewDialog.transaction}
        config={viewDialog.transaction?._config}
        timeFormat={timeFormat}
      />
    </Box>
  );
}
